import HlsProvider from './hls/hls.provider';
import EventBus, { Event, Unsubscriber } from './event-bus';
import PlayerStateRepository, { PlayerState } from './player-state';
import {
    alwaysShowControlBarHandler,
    changeVolumeHandler,
    enterFullScreenHandler,
    enterNativeFullScreenHandler,
    exitFullScreenHandler,
    exitNativeFullScreenHandler,
    muteHandler,
    pauseHandler,
    playHandler,
    seekHandler,
    unmuteHandler
} from './controller-internals/action-handlers';
import { Metadata, setupMetadataManagement, Tag } from './controller-internals/metadata-management';
import {
    detectSubtitleTracks,
    disableSubtitles,
    disableSubtitlesHandler,
    selectSubtitleTrackHandler
} from './controller-internals/subtitles-management';
import BaseCoreController from '@/core/base-core.controller';
import { cloneDeep, isFunction } from 'lodash';
import { HlsConfig } from 'hls.js';
import { HiveConfig } from '@/core/hls/plugins/hive';
import { nowTimeInHHMMSSsss } from '@/common/time';

interface CoreControllerConfig {
    alwaysCollapseControlBarOnIdle: boolean;
    playerElement: Element;
    videoElement: HTMLVideoElement;
    sourceUrl: string;
    eventBus: EventBus;
    playerStateRepository: PlayerStateRepository;
    useCredentials: boolean;
    restrictMetadata: boolean;
    metadataTypes: string[];
    contentId: string;
    cmcdSessionId: string;
    hlsConfigOverwrites: Partial<HlsConfig>;
    hiveConfig: HiveConfig;
}

export default class CoreController extends BaseCoreController {
    private unsubscribers : Unsubscriber[] = [];

    private hlsProvider: HlsProvider;

    private lastEmittedTag: Tag = null as unknown as Tag;

    private playerElement: Element;

    private video: HTMLVideoElement;

    private src: string;

    private expectingPauseEvent = false;

    private disposing = false;

    constructor({
        playerElement,
        videoElement,
        sourceUrl,
        eventBus,
        playerStateRepository,
        useCredentials,
        restrictMetadata,
        metadataTypes,
        contentId,
        cmcdSessionId,
        hlsConfigOverwrites,
        hiveConfig,
        alwaysCollapseControlBarOnIdle
    }: CoreControllerConfig) {
        super(eventBus, playerStateRepository, alwaysCollapseControlBarOnIdle);

        this.playerElement = playerElement;
        this.video = videoElement;
        this.src = sourceUrl;

        this.hlsProvider = new HlsProvider({
            eventBus,
            playerStateRepository,
            useCredentials,
            contentId,
            cmcdSessionId,
            hlsConfigOverwrites,
            hiveConfig
        });

        if (this.playerElement) {
            this.setupPlayerElement();
        }

        if (this.video) {
            this.setupVideo();
            this.setupVideoTracks(restrictMetadata, metadataTypes);
        }

        this.setupEventListeners();

        console.debug('[VideoPlayerCore] controller created');
    }

    public updateOverrides(hlsConfigOverrides: Partial<HlsConfig>): void {
        this.hlsProvider?.updateOverrides?.(hlsConfigOverrides);
    }

    public async init(): Promise<void> {
        if (this.wasInitialized) {
            return;
        }

        this.wasInitialized = true;
        if (this.hlsProvider.isSrcSupported(this.src)) {
            await this.setupHls(this.src);
        } else if (this.video) {
            // It seems that if the source url is set after the video element is created, you need a call to load() before it can be played.
            // This was not needed previously because we were always recreating a new video element on each source change
            this.video.load();
        }

        this.setMuted(this.playerStateRepository.state.muted);

        console.debug(`[VideoPlayerCore] controller initialized at ${nowTimeInHHMMSSsss()}`);
    }

    public tryAutoplay(): void {
        console.log(`[VideoPlayerCore] trying to autoplay at ${nowTimeInHHMMSSsss()}`);

        // On safari ios if video.play() is called right away it might hang forever. The reason was unknown at the time of this writing.
        // Calling video.play() on nextTick() doesn't work, a setTimeout seem to do the trick
        setTimeout(async () => {
            try {
                if (this.disposing) {
                    return;
                }
                // If this play() call hangs forever, it means there's probably a missing call to video.load() before it,
                // either by hls.js, or in init() above
                await this.video.play();
                this.sendAutoplayedEvent({ muted: false });
                console.log(`[VideoPlayerCore] successfully autoplayed with sound at ${nowTimeInHHMMSSsss()}`);
            } catch (_) {
                console.warn(`[VideoPlayerCore] failed to autoplay, play again muted at ${nowTimeInHHMMSSsss()}`);

                this.setMuted(true);

                try {
                    if (this.disposing) {
                        return;
                    }
                    await this.video.play();
                    console.log(`[VideoPlayerCore] successfully autoplayed muted at ${nowTimeInHHMMSSsss()}`);
                    this.sendAutoplayedEvent({ muted: true });
                } catch (e) {
                    console.error(`[VideoPlayerCore] could not autoplay muted at ${nowTimeInHHMMSSsss()}`, e);
                    this.setMuted(false);
                }
            }
        });
    }

    private sendAutoplayedEvent({ muted = false } = {}) {
        this.eventBus.emit(Event.customAction, { muted }, 'autoplayed');
    }

    public dispose(): void {
        super.dispose();
        this.disposing = true;
        this.hlsProvider.dispose();
        this.disposeEventListeners();
    }

    // private methods

    private async setupHls(src: string) {
        await this.hlsProvider.attachMedia(this.video)
            .then(console.log)
            .catch(console.error);

        await this.hlsProvider.loadSource(this.video, src)
            .then(console.log)
            .catch(console.error);
    }

    private setupPlayerElement(): void {
        this.playerElement.onfullscreenchange = () => {
            this.updateState({ isNativeFullScreen: !!document.fullscreenElement });
        };
    }

    private setupVideo(): void {
        this.video.onplay = () => this.setIsPlaying(true);
        this.video.onpause = () => this.setIsPlaying(false);
        this.video.onerror = (event: unknown) => this.onVideoElementError(event);
        this.video.onloadedmetadata = () => {
            this.updateState({ videoLoaded: true });
        };
        this.video.ontimeupdate = () => {
            const videoElapsedSecs = this.video.currentTime;
            const hasPlayed = this.playerStateRepository.state.hasPlayed || videoElapsedSecs > 0;
            this.updateState({ videoElapsedSecs, hasPlayed });
        };
        this.video.ondurationchange = () => {
            const videoDurationSecs = this.video.duration;
            this.updateState({ videoDurationSecs });
        };
        this.video.onresize = () => {
            const videoHeight = this.video.videoHeight;
            this.updateState({ videoHeight });
        };
        // Prevent right-clicks on the video, otherwise you have the possibility to show the default video controls of the browser
        // To avoid blocking right-clicking on the whole player, which may annoy some users, a default invisible but clickable
        // widget also covers the video (invisible-overlay)
        this.video.oncontextmenu = (e) => e.preventDefault();
    }

    private setupVideoTracks(restrictMetadata: boolean, metadataTypes: string[]): void {
        this.video.textTracks.onaddtrack = (ev) => {
            setupMetadataManagement(
                ev?.track,
                restrictMetadata,
                metadataTypes,
                this.emitMetadata.bind(this)
            );
            detectSubtitleTracks(this.video.textTracks, this.updateState.bind(this));
            disableSubtitles(this.video.textTracks, this.updateState.bind(this));
        };

        this.unsubscribers.push(
            this.eventBus.on(Event.seek, () => {
                this.lastEmittedTag = null as unknown as Tag;
            })
        );
    }

    private setupEventListeners(): void {
        this.unsubscribers.push(
            playHandler(this.eventBus, this.video, this.init.bind(this)),
            pauseHandler(this.eventBus, this.video, (val:boolean) => this.expectingPauseEvent = val),
            enterFullScreenHandler(this.eventBus, this.updateState.bind(this)),
            exitFullScreenHandler(this.eventBus, this.updateState.bind(this)),
            alwaysShowControlBarHandler(this.eventBus, this.updateState.bind(this)),
            enterNativeFullScreenHandler(this.eventBus, this.playerElement),
            exitNativeFullScreenHandler(this.eventBus),
            seekHandler(this.eventBus, this.video, this.updateState.bind(this)),
            muteHandler(this.eventBus, this.setMuted.bind(this)),
            unmuteHandler(this.eventBus, this.setMuted.bind(this)),
            changeVolumeHandler(this.eventBus, this.video, this.updateState.bind(this)),
            selectSubtitleTrackHandler(this.eventBus, this.video, this.updateState.bind(this)),
            disableSubtitlesHandler(this.eventBus, this.video, this.updateState.bind(this))
        );
    }

    private disposeEventListeners(): void {
        for (const unsubscriber of this.unsubscribers) {
            if (isFunction(unsubscriber)) {
                unsubscriber();
            }
        }

        this.unsubscribers = [];
    }

    private setMuted(value: boolean): void {

        this.updateState({ muted: value });

        if (this.video.muted === value) {
            console.debug(`[VideoPlayerCore] skip ${value ? 'muting' : 'unmuting'} because it's already the case at ${nowTimeInHHMMSSsss()}`);
            return;
        }

        console.debug(`[VideoPlayerCore] set ${value ? 'muted' : 'unmuted'} at ${nowTimeInHHMMSSsss()}`);

        this.video.muted = value;
    }

    private onVideoElementError(event: unknown): void {
        console.error(`[VideoPlayerCore] video element error at ${nowTimeInHHMMSSsss()}s`, event);
    }

    private setIsPlaying(isPlaying: boolean): void {
        const state = cloneDeep(this.playerStateRepository?.state) as Partial<PlayerState> | undefined;
        delete state?.levelsAvailable;
        delete state?.subtitleTracks;
        delete state?.subtitleTrackActive;
        delete state?.subtitles;
        delete state?.currentLevel;
        delete state?.isControlBarVisible;
        console.debug(`[VideoPlayerCore] video ${isPlaying ? 'playing' : 'paused'} at ${nowTimeInHHMMSSsss()}`, JSON.stringify(state));

        const videoElapsedSecs = this.video.currentTime;

        this.updateState({ isPlaying, videoElapsedSecs });

        const currentState = this.playerStateRepository.state;

        const hasReachedEnd = currentState.videoElapsedSecs >= currentState.videoDurationSecs;

        if (!isPlaying && !this.expectingPauseEvent && !hasReachedEnd) {
            // The browser paused the video unexpectedly, maybe because we tried unmuting before user
            // interaction and can-autoplay failed to return the correct response in Countdown.vue
            // that occurs frequently in the e2e test, particularly with custom domain enabled
            if (currentState.muted) {
                // we might get here if the user unplugged their headphones
                console.warn('[VideoPlayerCore] video was paused without user request, and player is muted');
            } else {
                console.warn(`[VideoPlayerCore] video was paused without user request, trying to resume at ${nowTimeInHHMMSSsss()}`);
                this.tryAutoplay();
            }
        }

        this.expectingPauseEvent = false;
    }

    private emitMetadata(metadata: Metadata): void {
        const tag = metadata.value || {} as Tag;

        const newTagTimestamp = tag.timestamp || 0;
        const lastTagTimestamp = this.lastEmittedTag?.timestamp || 0;
        // With old livestreams, there is no timestamp in the tag, those should still be emitted.
        const isOldTag = lastTagTimestamp && newTagTimestamp && newTagTimestamp < lastTagTimestamp;

        if (isOldTag) {
            return;
        }

        this.lastEmittedTag = tag;

        this.eventBus.emit(
            Event.metadata,
            metadata
        );
    }
}
